<template>
  <div class="device-adv-panel">
    <p class="text-caption text-medium-emphasis mb-4">
      按分组维护无功模式、容量占比、自动需量及通信中断时的储能运行方式；右侧为操作留痕（演示数据与本地记录，未接真实接口）。
    </p>

    <v-row class="adv-layout" align="start">
      <v-col cols="12" lg="8">
        <!-- 分组信息 -->
        <v-card variant="outlined" rounded="lg" class="adv-section gm-card pa-4 pa-md-5 mb-4">
          <div class="text-subtitle-1 font-weight-semibold mb-4">分组信息</div>

          <div class="group-info-grid rounded-lg pa-4 mb-4">
            <div class="group-info-row">
              <span class="group-info-k text-medium-emphasis">eLink标识</span>
              <span class="group-info-v font-weight-medium">{{ elinkId }}</span>
            </div>
            <v-divider class="my-3 opacity-50" />
            <div class="group-info-row">
              <span class="group-info-k text-medium-emphasis">容量(kW)</span>
              <span class="group-info-v font-weight-medium">{{ capacityKw }}</span>
            </div>
            <v-divider class="my-3 opacity-50" />
            <div class="group-info-row">
              <span class="group-info-k text-medium-emphasis">防逆流</span>
              <span class="group-info-v font-weight-medium">{{ antiRefluxEnabled ? '开启' : '关闭' }}</span>
            </div>
          </div>

          <v-select
            v-model="reactivePowerMode"
            :items="reactiveItems"
            item-title="title"
            item-value="value"
            label="无功工作模式"
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            rounded="lg"
          />
        </v-card>

        <!-- 参数设置 -->
        <v-card variant="outlined" rounded="lg" class="adv-section gm-card pa-4 pa-md-5 mb-4">
          <div class="text-subtitle-1 font-weight-semibold mb-4">参数设置</div>

          <div class="d-flex flex-wrap align-center ga-3 mb-4">
            <span class="text-body-2 text-medium-emphasis">箱变容量变比</span>
            <div class="ratio-stepper d-inline-flex align-center rounded-lg">
              <v-btn
                icon
                variant="text"
                density="comfortable"
                aria-label="减小"
                :disabled="ratedCapacityRatio <= RATIO_MIN"
                @click="bumpRatio(-RATIO_STEP)"
              >
                <v-icon>mdi-minus</v-icon>
              </v-btn>
              <span class="ratio-stepper__value text-body-1 font-weight-semibold px-2">{{ ratioDisplay }}</span>
              <v-btn
                icon
                variant="text"
                density="comfortable"
                aria-label="增大"
                :disabled="ratedCapacityRatio >= RATIO_MAX"
                @click="bumpRatio(RATIO_STEP)"
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </div>
          </div>

          <v-btn color="primary" variant="flat" rounded="lg" class="text-none" :loading="savingParams" @click="onSaveParams">
            保存参数设置
          </v-btn>
        </v-card>

        <!-- 自动需量 -->
        <v-card variant="outlined" rounded="lg" class="adv-section gm-card pa-4 pa-md-5 mb-4">
          <div class="text-subtitle-1 font-weight-semibold mb-4">自动需量</div>
          <v-switch
            v-model="autoDemand"
            color="primary"
            hide-details
            density="comfortable"
            inset
            label="自动需量"
            @update:model-value="onAutoDemandChange"
          />
        </v-card>

        <!-- 通信中断储能运行模式 -->
        <v-card variant="outlined" rounded="lg" class="adv-section gm-card pa-4 pa-md-5">
          <div class="text-subtitle-1 font-weight-semibold mb-4">通信中断储能运行模式</div>

          <v-radio-group v-model="commInterruptMode" hide-details class="comm-radio-group">
            <v-radio
              v-for="opt in commModeOptions"
              :key="opt.value"
              :value="opt.value"
              :label="opt.label"
              color="primary"
              class="comm-radio"
            />
          </v-radio-group>

          <v-btn
            color="primary"
            variant="flat"
            rounded="lg"
            class="text-none mt-2"
            :loading="savingRunMode"
            @click="onSaveRunMode"
          >
            保存运行模式
          </v-btn>
        </v-card>
      </v-col>

      <v-col cols="12" lg="4">
        <v-card variant="outlined" rounded="lg" class="history-card gm-card pa-4">
          <div class="text-subtitle-1 font-weight-semibold mb-1">高级配置操作历史</div>
          <p class="text-caption text-medium-emphasis mb-4">最近 {{ historyEntries.length }} 条</p>

          <div class="history-scroll">
            <div v-for="item in historyEntries" :key="item.id" class="history-item">
              <div class="history-item__rail" aria-hidden="true" />
              <div class="history-item__body">
                <div class="text-caption text-medium-emphasis">
                  操作人：<span class="text-body-2 text-high-emphasis">{{ item.operator }}</span>
                </div>
                <div class="text-body-2 mt-1">{{ item.content }}</div>
                <div class="text-caption text-medium-emphasis mt-2">{{ item.time }}</div>
              </div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import dayjs from 'dayjs'
import { eui } from 'ems-ui'

const RATIO_MIN = 0.01
const RATIO_MAX = 1
const RATIO_STEP = 0.01

/** 演示只读分组字段（可与接入拓扑后接口数据对齐） */
const elinkId = ref('A2211E20S0331')
const capacityKw = ref(2299)
const antiRefluxEnabled = ref(true)

const reactiveItems = [
  { title: '不出无功', value: 'none' },
  { title: '优先出无功', value: 'pfc' },
  { title: '优先出有功', value: 'pfp' },
]

const reactivePowerMode = ref('none')

const ratedCapacityRatio = ref(0.9)
const ratioDisplay = computed(() => ratedCapacityRatio.value.toFixed(2))

const autoDemand = ref(false)

const commModeOptions = [
  { value: 'always', label: '按策略一直运行' },
  { value: 'next_day_stop', label: '按策略运行第二天停机' },
  { value: 'work_rest', label: '按策略工作休息制度运行' },
]

const commInterruptMode = ref('work_rest')

let historyIdSeq = 0

function nextHistoryId() {
  historyIdSeq += 1
  return historyIdSeq
}

function formatNow() {
  return dayjs().format('YYYY-MM-DD HH:mm:ss')
}

function reactiveModeTitle(value) {
  return reactiveItems.find((x) => x.value === value)?.title ?? value
}

function commModeHistoryText(value) {
  return commModeOptions.find((x) => x.value === value)?.label ?? value
}

function pushHistory(operator, content) {
  historyEntries.value.unshift({
    id: nextHistoryId(),
    operator,
    content,
    time: formatNow(),
  })
  const max = 80
  if (historyEntries.value.length > max) {
    historyEntries.value.length = max
  }
}

/** 与原型一致的操作人示例 + 若干条种子记录 */
const DEMO_OPERATOR = '13087516801'

const historyEntries = ref([
  {
    id: nextHistoryId(),
    operator: '13087516801',
    content: '通信中断储能停机模式修改为:按策略工作休息制度运行',
    time: '2026-05-09 18:55:44',
  },
  {
    id: nextHistoryId(),
    operator: '15291911314',
    content: '额定容量占比修改为:0.88',
    time: '2026-05-09 17:12:03',
  },
  {
    id: nextHistoryId(),
    operator: '13087516801',
    content: '无功工作模式修改为:不出无功',
    time: '2026-05-08 09:40:21',
  },
])

const savingParams = ref(false)
const savingRunMode = ref(false)

function bumpRatio(delta) {
  const v = Math.round((ratedCapacityRatio.value + delta) * 100) / 100
  ratedCapacityRatio.value = Math.min(RATIO_MAX, Math.max(RATIO_MIN, v))
}

async function onSaveParams() {
  savingParams.value = true
  try {
    await new Promise((r) => setTimeout(r, 280))
    pushHistory(
      DEMO_OPERATOR,
      `保存参数设置：额定容量占比 ${ratioDisplay.value}，无功工作模式 ${reactiveModeTitle(reactivePowerMode.value)}`,
    )
    eui.showToast('参数设置已保存（演示，未调用接口）', 'success')
  } finally {
    savingParams.value = false
  }
}

function onAutoDemandChange(val) {
  pushHistory(DEMO_OPERATOR, `自动需量已${val ? '开启' : '关闭'}`)
  eui.showToast(`自动需量已${val ? '开启' : '关闭'}（演示）`, 'info')
}

async function onSaveRunMode() {
  savingRunMode.value = true
  try {
    await new Promise((r) => setTimeout(r, 280))
    const text = commModeHistoryText(commInterruptMode.value)
    pushHistory(DEMO_OPERATOR, `通信中断储能停机模式修改为:${text}`)
    eui.showToast('运行模式已保存（演示，未调用接口）', 'success')
  } finally {
    savingRunMode.value = false
  }
}

defineExpose({
  getDraft: () => ({
    panel: 'device_run_advanced',
    grouping: {
      elinkId: elinkId.value,
      capacityKw: capacityKw.value,
      antiRefluxEnabled: antiRefluxEnabled.value,
      reactivePowerMode: reactivePowerMode.value,
    },
    ratedCapacityRatio: ratedCapacityRatio.value,
    autoDemand: autoDemand.value,
    commInterruptRunMode: commInterruptMode.value,
    operationHistory: historyEntries.value.map(({ operator, content, time }) => ({
      operator,
      content,
      time,
    })),
  }),
})
</script>

<style scoped lang="scss">
.device-adv-panel {
  max-width: 1200px;
}

.group-info-grid {
  background: rgba(var(--v-theme-primary), 0.06);
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.group-info-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px 16px;
}

.group-info-k {
  flex: 0 0 96px;
  font-size: 0.8125rem;
}

.group-info-v {
  min-width: 0;
  word-break: break-all;
}

.ratio-stepper {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgb(var(--v-theme-surface));
}

.ratio-stepper__value {
  min-width: 3.25rem;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.comm-radio-group :deep(.v-selection-control) {
  align-items: flex-start;
}

.comm-radio {
  margin-bottom: 4px;
}

.history-card {
  position: sticky;
  top: 8px;
  max-height: min(720px, calc(100vh - 140px));
  display: flex;
  flex-direction: column;
}

.history-scroll {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding-right: 4px;
}

.history-item {
  position: relative;
  display: flex;
  gap: 12px;
  padding-bottom: 16px;
}

.history-item:last-child {
  padding-bottom: 0;
}

.history-item__rail {
  flex: 0 0 10px;
  position: relative;
  margin-top: 4px;
  border-radius: 999px;
  background: rgba(var(--v-theme-primary), 0.35);
  align-self: stretch;
  min-height: 100%;
}

.history-item__rail::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: -16px;
  width: 0;
  height: 16px;
  border-left: 2px dashed rgba(var(--v-border-color), 0.45);
  transform: translateX(-50%);
}

.history-item:last-child .history-item__rail::after {
  display: none;
}

.history-item__body {
  flex: 1;
  min-width: 0;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(var(--v-border-color), 0.35);
}

.history-item:last-child .history-item__body {
  border-bottom: none;
}
</style>
